import { Box, CircularProgress, Paper, Switch, Typography } from '@mui/material';
import { useAdminTheme } from '../../admin-theme';

// "Variant options" card (Tags tab): one switch per variant option name. On = that
// option's values become tags at sync (and on "Re-apply to pending"). Off = they don't.

export interface OptionRole { optionName: string; enabled: boolean; productCount: number }

/** What every tag-config mutation returns once the pending queue was re-tagged. */
export interface RefreshResult { refreshed?: number; withdrawn?: number; skippedStale?: number; liveUpdated?: number; refreshError?: string }

/** Snackbar text and type for a mutation's refresh counts (a refresh failure is a warning). */
export function refreshNotice(r: RefreshResult | undefined, saved = 'Saved'): { message: string; type: 'success' | 'warning' } {
  if (r?.refreshError) {
    return { message: `${saved}, but re-tagging pending items failed: ${r.refreshError}. Use "Re-apply to pending" to retry.`, type: 'warning' };
  }
  const refreshed = r?.refreshed ?? 0;
  const withdrawn = r?.withdrawn ?? 0;
  const skipped = r?.skippedStale ?? 0;
  // skipped = rows staged before tag sources were recorded; a Vend re-sync brings them up to date
  const extra = (withdrawn ? `, ${withdrawn} withdrawn` : '')
    + (skipped ? `, ${skipped} skipped (re-sync needed)` : '');
  const live = r?.liveUpdated ?? 0;
  const liveText = live ? `${live} live product${live === 1 ? '' : 's'} and ` : '';
  return { message: `${saved}. ${liveText}${refreshed} pending item${refreshed === 1 ? '' : 's'} updated${extra}.`, type: 'success' };
}

interface Props {
  options: OptionRole[];
  loading: boolean;
  savingName: string | null;
  onToggle: (optionName: string, enabled: boolean) => void;
}

export default function OptionTagSwitches({ options, loading, savingName, onToggle }: Props) {
  const { colors } = useAdminTheme();
  return (
    <Paper sx={{ p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary }}>Variant options</Typography>
      <Typography variant="caption" sx={{ color: colors.textMuted }}>
        Switch an option on to turn its values (for example 3mg) into tags. Products is how many staged products have the option.
      </Typography>
      {loading ? (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
      ) : options.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 1, color: colors.textMuted }}>No variant options yet.</Typography>
      ) : (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column' }}>
          {options.map((o) => (
            <Box key={o.optionName} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.25 }}>
              <Switch size="small" checked={o.enabled} disabled={savingName === o.optionName}
                onChange={(e) => onToggle(o.optionName, e.target.checked)}
                slotProps={{ input: { 'aria-label': `${o.optionName} tags` } }} />
              <Typography variant="body2" sx={{ flex: 1, color: colors.textPrimary }}>{o.optionName}</Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>{o.productCount}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}
